import { useEffect, useRef } from 'react';
import { subscribe } from '../services/dataSyncBus';

/**
 * Subscribe a React component to one or more data-sync events.
 *
 * Calls `callback(payload)` whenever an event of interest fires.
 * The callback is **debounced** (default 250 ms) so a burst of rapid emits
 * (e.g. bulk delete of 20 payments) only triggers ONE refetch.
 *
 * Usage:
 *   import { useDataSync } from '../../hooks/useDataSync';
 *   import { EVT } from '../../services/dataSyncBus';
 *
 *   useDataSync([EVT.PAYMENT, EVT.TENANT], fetchData);
 *
 * Tips:
 *   • Always pass a stable callback (wrap `fetchData` in useCallback).
 *   • For a heavier refresh, bump debounce: useDataSync(EVT.X, fn, { debounceMs: 750 }).
 */
export const useDataSync = (events, callback, options = {}) => {
  const { debounceMs = 250 } = options;
  const cbRef = useRef(callback);
  cbRef.current = callback;

  // Memoize events list as a stable string so the effect doesn't churn on every render
  const eventsKey = Array.isArray(events) ? events.join('|') : String(events);

  useEffect(() => {
    let timeout = null;
    const handler = (payload) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        cbRef.current?.(payload);
      }, debounceMs);
    };
    const unsub = subscribe(Array.isArray(events) ? events : [events], handler);
    return () => {
      if (timeout) clearTimeout(timeout);
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsKey, debounceMs]);
};
